/**
 * OAuth connector — branded-login bridge (ChatGPT / remote MCP clients).
 *
 * When a remote MCP client (ChatGPT Desktop, etc.) starts the OAuth flow, the
 * ProMedic MCP server's `/oauth/authorize` bounces the browser to this SPA's
 * `/oauth-continue?tx=<tx>` route so the user logs in with the SAME branded
 * `/login` they already know (mirrors the Kane SSO handoff in KaneLaunch.js).
 *
 * Once the user has a ProMedic session, this service posts the opaque `tx`
 * (signed by the MCP server, never parsed here) to the MCP `/oauth/mint-code`
 * endpoint using the user's Supabase access token as the bearer — the exact
 * trust path the Kane endpoints use. The MCP re-mints an independent session
 * server-side (refresh tokens NEVER enter the frontend), issues a single-use
 * authorization code, and returns the client redirect for us to follow.
 */

import { supabase } from './supabase';

const MCP_URL = process.env.REACT_APP_MCP_SERVER_URL;

/**
 * Exchange the OAuth transaction for the client redirect URL.
 *
 * @param {string} tx opaque transaction token from the /oauth-continue query
 * @returns {Promise<string>} the URL to redirect the browser to (the OAuth
 *   client's callback with `?code=…&state=…`)
 * @throws {Error} when there is no session, the server is misconfigured, or the
 *   mint fails.
 */
export async function mintOauthRedirect(tx) {
  if (!tx) throw new Error('Falta el parámetro de autorización.');
  if (!MCP_URL) throw new Error('REACT_APP_MCP_SERVER_URL not configured');

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session');

  const res = await fetch(`${MCP_URL}/oauth/mint-code`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tx }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error_description || body?.error || `Mint failed (${res.status})`);
  }

  const body = await res.json();
  if (!body?.redirect) throw new Error('Mint response missing redirect');
  return body.redirect;
}
